import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Alert } from 'components/common/modal/Alert';
import { Confirm } from 'components/common/modal/Confirm';

interface AlertOptions {
    title?: string;
    message: string;
    confirmText?: string;
}

interface ConfirmOptions {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
}

interface AlertContextType {
    showAlert: (options: AlertOptions) => Promise<void>;
    showConfirm: (options: ConfirmOptions) => Promise<boolean>;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within AlertProvider');
    }
    return context;
};

interface AlertProviderProps {
    children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
    const [alertState, setAlertState] = useState({
        isOpen: false,
        title: '알림',
        message: '',
        confirmText: '확인',
        onConfirm: () => {}
    });

    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: '확인',
        message: '',
        confirmText: '확인',
        cancelText: '취소',
        onConfirm: () => {},
        onCancel: () => {}
    });

    const showAlert = (options: AlertOptions): Promise<void> => {
        return new Promise((resolve) => {
            setAlertState({
                isOpen: true,
                title: options.title || '알림',
                message: options.message,
                confirmText: options.confirmText || '확인',
                onConfirm: () => {
                    setAlertState((prev) => ({ ...prev, isOpen: false }));
                    resolve();
                }
            });
        });
    };

    const showConfirm = (options: ConfirmOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfirmState({
                isOpen: true,
                title: options.title || '확인',
                message: options.message,
                confirmText: options.confirmText || '확인',
                cancelText: options.cancelText || '취소',
                onConfirm: () => {
                    setConfirmState((prev) => ({ ...prev, isOpen: false }));
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmState((prev) => ({ ...prev, isOpen: false }));
                    resolve(false);
                }
            });
        });
    };

    return (
        <AlertContext.Provider value={{ showAlert, showConfirm }}>
            {children}
            <Alert
                isOpen={alertState.isOpen}
                title={alertState.title}
                message={alertState.message}
                confirmText={alertState.confirmText}
                onConfirm={alertState.onConfirm}
            />
            <Confirm
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                message={confirmState.message}
                confirmText={confirmState.confirmText}
                cancelText={confirmState.cancelText}
                onConfirm={confirmState.onConfirm}
                onCancel={confirmState.onCancel}
            />
        </AlertContext.Provider>
    );
};
